import LocationOnIcon from '@mui/icons-material/LocationOn'
import { Box, Card, CardContent, Checkbox, FormControl, FormControlLabel, Typography } from '@mui/material'
import Autocomplete from '@mui/material/Autocomplete'
import Grid from '@mui/material/Grid'
import TextField from '@mui/material/TextField'
import { debounce } from '@mui/material/utils'
import parse from 'autosuggest-highlight/parse'
import React, { useEffect, useState } from 'react'
import { useLocation, useNavigate, createSearchParams } from 'react-router-dom'
import { GOOGLE_MAPS_API_KEY, PlaceType, autocompleteService, loadScript } from '../utils/GoogleMapsService'
import { grey, pink } from '@mui/material/colors'

const useQuery = () => new URLSearchParams(useLocation().search)
export interface AddressSearchBoxProps {
  onAddressSelect: (selectedAddress: any, search: string, beds?: string, baths?: string, sqft?: string) => void
  credits: number | undefined
  showCredits: boolean
  orgCredits?: number
}

const AddressSearchBox = (props: AddressSearchBoxProps) => {
  const [value, setValue] = React.useState<PlaceType | null>(null)
  const [inputValue, setInputValue] = React.useState('')
  const [options, setOptions] = React.useState<readonly PlaceType[]>([])
  const loaded = React.useRef(false)
  const [showMsg, setShowMsg] = useState(new Date() < new Date('2023-07-05'))
  const query = useQuery()
  const [beds, setBeds] = useState(query.get('beds'))
  const [baths, setBaths] = useState(query.get('baths'))
  const [sqft, setSqft] = useState(query.get('sqft'))
  const [advanced, setAdvanced] = useState<boolean>((beds || baths || sqft) ? true : false)

  if (typeof window !== 'undefined' && !loaded.current) {
    if (!document.querySelector('#google-maps')) {
      loadScript(
        `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places`,
        document.querySelector('head'),
        'google-maps', () => { }
      )
    }
    loaded.current = true
  }

  const fetch = React.useMemo(
    () =>
      debounce(
        (
          request: { input: string },
          callback: (results?: readonly PlaceType[]) => void,
        ) => {
          (autocompleteService.current as any).getPlacePredictions({
            ...request,
            // https://developers.google.com/maps/documentation/javascript/reference/places-autocomplete-service#AutocompletionRequest
            componentRestrictions: { country: 'us' }
          }, callback)
        },
        400,
      ),
    [],
  )

  useEffect(() => {
    let active = true
    if (!autocompleteService.current && (window as any).google) {
      autocompleteService.current = new (window as any).google.maps.places.AutocompleteService()
    }
    if (!autocompleteService.current) {
      return undefined
    }

    if (inputValue === '') {
      setOptions(value ? [value] : [])
      return undefined
    }

    fetch({ input: inputValue }, (results?: readonly PlaceType[]) => {
      if (active) {
        let newOptions: readonly PlaceType[] = []

        if (value) {
          newOptions = [value]
        }

        if (results) {
          newOptions = [...newOptions, ...results]
        }
        setOptions(newOptions)
      }
    })

    return () => {
      active = false
    }
  }, [value, inputValue, fetch])
  return (
    <Box sx={{
      width: '100%',
      textAlign: 'center',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      alignContent: 'center',
      flexDirection: 'column'
    }} mt={2}>

      {showMsg && <Box sx={{ width: '100%', textAlign: 'center', color: 'warning.main' }}>
        <Typography variant="caption" gutterBottom>
          <strong>Note: </strong>There were some errors in the past week due to a missing integration, that has been fixed as of 07/03/2023.
        </Typography>
      </Box>}

      <Autocomplete data-tour='address-input'
        id="google-map-demo"
        sx={{ width: 350, marginTop: 3 }}
        getOptionLabel={(option) =>
          typeof option === 'string' ? option : option.description
        }
        filterOptions={(x) => x}
        options={options}
        autoComplete
        includeInputInList
        filterSelectedOptions
        value={value}
        noOptionsText="Start typing to see/pick an address"
        onChange={(event: any, newValue: PlaceType | null) => {
          setOptions(newValue ? [newValue, ...options] : options)
          setValue(newValue)
          const geocoder = new (window as any).google.maps.Geocoder()
          geocoder.geocode({ placeId: newValue?.place_id }, (results: any, status: any) => {
            if (status === 'OK') {
              console.log(`Geocode was success:  ${JSON.stringify(results)}`)
              props.onAddressSelect('newanalysis', createSearchParams({ beds: beds ? beds : '', baths: baths ? baths : '', sqft: sqft ? sqft : '', lat: results[0].geometry?.location?.lat(), lng: results[0].geometry?.location?.lng(), fullAddress: results[0].formatted_address}).toString(), beds ? beds : '', baths ? baths : '', sqft ? sqft : '')
            } else {
              console.log(`Geocode was NOT success: ${status}`)
              props.onAddressSelect(newValue?.description, createSearchParams({ beds: beds ? beds : '', baths: baths ? baths : '', sqft: sqft ? sqft : '' }).toString(), beds ? beds : '', baths ? baths : '', sqft ? sqft : '')
            }
          })
        }}
        onInputChange={(event, newInputValue) => {
          setInputValue(newInputValue)
        }}
        renderInput={(params) => (
          <TextField {...params} label="Address Search" fullWidth />
        )}
        renderOption={(props, option) => {
          const matches =
            option.structured_formatting.main_text_matched_substrings || []

          const parts = parse(
            option.structured_formatting.main_text,
            matches.map((match: any) => [match.offset, match.offset + match.length]),
          )

          return (
            <li {...props}>
              <Grid container alignItems="center">
                <Grid item sx={{ display: 'flex', width: 44 }}>
                  <LocationOnIcon sx={{ color: 'text.secondary' }} />
                </Grid>
                <Grid item sx={{ width: 'calc(100% - 44px)', wordWrap: 'break-word' }}>
                  {parts.map((part: any, index: number) => (
                    <Box
                      key={index}
                      component="span"
                      sx={{ fontWeight: part.highlight ? 'bold' : 'regular' }}
                    >
                      {part.text}
                    </Box>
                  ))}
                  <Typography variant="body2" color="text.secondary">
                    {option.structured_formatting.secondary_text}
                  </Typography>
                </Grid>
              </Grid>
            </li>
          )
        }}
      />

      <Card sx={{ width: 375, border: 0, boxShadow: 0 }}>
        <CardContent>
          <FormControl sx={{ width: '100%', textAlign: 'left' }} data-tour='address-customize'>
            <FormControlLabel
              label={<Box sx={{ display: 'flex', alignItems: 'flex-end' }}>
                <Typography variant='body1'>Override Beds, Baths or Sqft.</Typography>
                <Typography variant='caption' sx={{ color: grey[500] }}>(optional)</Typography>
              </Box>}
              control={<Checkbox
                checked={advanced ? true : false}
                onChange={(e) => { setAdvanced(e.target.checked) }}
                inputProps={{ 'aria-label': 'controlled' }} />} />
          </FormControl>

          {advanced && <Box sx={{ width: '100%', textAlign: 'left' }}>
            <FormControl sx={{ width: '100%', textAlign: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-end' }}>
                <FormControl sx={{ m: 1, ml: 0, width: '9ch' }} variant="outlined">
                  <TextField id="customize-beds" label="Beds" variant="outlined" defaultValue={beds}
                    onChange={(event) => setBeds(event.target.value)} />
                </FormControl>
                <FormControl sx={{ m: 1, ml: 0, width: '9ch' }} variant="outlined">
                  <TextField id="customize-baths" label="Baths" variant="outlined" defaultValue={baths}
                    onChange={(event) => setBaths(event.target.value)} />
                </FormControl>
                <FormControl sx={{ m: 1, mx: 0, width: '15ch' }} variant="outlined">
                  <TextField id="customize-sqft" label="Sqft" variant="outlined" defaultValue={sqft}
                    onChange={(event) => setSqft(event.target.value)} />
                </FormControl>
              </Box>
            </FormControl>
          </Box>}
        </CardContent>
      </Card>

      {props.showCredits ? <>
        {props.credits ? <>
          {props.credits <= 0 ? <>
            {props.orgCredits && props.orgCredits > 0 ?
              <Typography mt={1} variant="caption" color="primary.light">Remaining: <strong>{props.orgCredits}</strong> (org) reports</Typography>
              : <Typography mt={1} variant="caption" color={pink[500]}>You have run out of credits, please pay to avoid interruptions <strong>{props.credits}</strong>.</Typography>}</>
            : <Typography mt={1} variant="caption" color="primary.light">Remaining: <strong>{props.credits}</strong> reports</Typography>}
        </> : <Typography mt={1} variant="caption" color="primary.light">~</Typography>}
      </> : <></>}
    </Box>
  )
}

export default AddressSearchBox